<template>
  <div class="vx-row">
    <div v-if="loading" class="loading-bar">
      <atom-spinner
        :animation-duration="1000"
        :size="60"
        color="#ff1d5e"
        class="mx-auto"
      />
    </div>
    <div v-else class="w-full">
      <vx-card>
        <div class="vx-row mb-3 row-align">
          <div class="vx-col w-full md:w-1/3 sm:w-full xs:w-full">
            <div class="dropdown-with-label">
              <small class="date-label">{{
                $t("accounting.label.accountname")
              }}</small>
              <v-select
                v-if="AllowedAccounts"
                label="name"
                v-model="selectedAccount"
                :options="AllowedAccounts"
                :placeholder="$t('placeholder.label.SelectOne')"
                :reduce="(obj) => obj.id"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="w-full font-semibold"
              ></v-select>
            </div>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-full xs:w-full">
            <div class="dropdown-with-label">
              <small class="date-label">{{
                $t("accounting.label.office")
              }}</small>
              <v-select
                label="name"
                v-if="OfficesList"
                v-model="selectedOffice"
                :options="OfficesList"
                :placeholder="$t('placeholder.label.SelectOne')"
                :reduce="(obj) => obj.id"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="w-full font-semibold"
              ></v-select>
            </div>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-full xs:w-full">
            <div class="dropdown-with-label">
              <small class="date-label">{{
                $t("accounting.label.filter")
              }}</small>
              <v-select
                label="text"
                v-model="selectedFilter"
                :options="filterList"
                :placeholder="$t('placeholder.label.SelectOne')"
                :reduce="(obj) => obj.value"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="w-full font-semibold"
              ></v-select>
            </div>
          </div>
          <vs-button
            icon-pack="feather"
            icon="icon-x"
            @click="handleClearAll"
            size="small"
            >{{ $t("accounting.label.clearall") }}</vs-button
          >
        </div>
        <div class="vx-row mb-3 row-align">
          <div class="vx-col w-full md:w-1/4 sm:w-full xs:w-full">
            <div class="dropdown-with-label">
              <small class="date-label">{{
                $t("accounting.label.fromdate")
              }}</small>
              <datepicker
                :placeholder="$t('placeholder.label.SelectDate')"
                v-model="fromDate"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="min-w-200 w-full"
              />
            </div>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-full xs:w-full">
            <div class="dropdown-with-label">
              <small class="date-label">{{
                $t("accounting.label.todate")
              }}</small>
              <datepicker
                :placeholder="$t('placeholder.label.SelectDate')"
                v-model="toDate"
                :dir="$vs.rtl ? 'rtl' : 'ltr'"
                class="min-w-200 w-full"
              />
            </div>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-full xs:w-full">
            <vs-input
              :label="$t('accounting.label.transactionid')"
              class="w-full font-semibold"
              v-model="transactionId"
            />
          </div>
          <vs-button
            icon-pack="feather"
            :disabled="isSearchDisabled"
            icon="icon-search"
            @click="handleSearch"
            size="small"
            >{{ $t("accounting.label.search") }}</vs-button
          >
        </div>
        <vs-table
          v-if="JournalEntriesList"
          :max-items="paginationItems[0]"
          pagination
          :data="JournalEntriesList"
        >
          <template slot="thead">
            <vs-th>{{ $t("accounting.label.entryid") }}</vs-th>
            <vs-th>{{ $t("accounting.label.entityname") }}</vs-th>
            <vs-th>{{ $t("accounting.label.transactiondate") }}</vs-th>
            <vs-th>{{ $t("accounting.label.transactionid") }}</vs-th>
            <vs-th>{{ $t("accounting.label.type") }}</vs-th>
            <vs-th>{{ $t("accounting.label.createdby") }}</vs-th>
            <vs-th>{{ $t("accounting.label.account") }}</vs-th>
            <vs-th>{{ $t("accounting.label.debit") }}</vs-th>
            <vs-th>{{ $t("accounting.label.credit") }}</vs-th>
            <vs-th class="tHeadCenter">{{
              $t("accounting.label.action")
            }}</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr :key="index" :data="data" v-for="(accounts, index) in data">
              <vs-td :data="accounts.id">{{ accounts.id }}</vs-td>
              <vs-td :data="accounts.officeName">{{
                accounts.officeName
              }}</vs-td>
              <vs-td :data="accounts.transactionDate">{{
                getDate(accounts.transactionDate)
              }}</vs-td>
              <vs-td :data="accounts.transactionId">{{
                accounts.transactionId
              }}</vs-td>
              <vs-td :data="accounts.glAccountType.value">{{
                accounts.glAccountType.value
              }}</vs-td>
              <vs-td :data="accounts.createdByUserName">{{
                accounts.createdByUserName
              }}</vs-td>
              <vs-td :data="accounts.glAccountName">{{
                accounts.glAccountName
              }}</vs-td>
              <vs-td>
                <template v-if="accounts.entryType.value === 'DEBIT'">
                  {{ accounts.amount }}
                </template>
                <template v-else> </template>
              </vs-td>
              <vs-td>
                <template v-if="accounts.entryType.value === 'CREDIT'">
                  {{ accounts.amount }}
                </template>
                <template v-else> </template>
              </vs-td>
              <vs-td class="whitespace-no-wrap">
                <div class="container_img">
                  <feather-icon
                    icon="EyeIcon"
                    title="View"
                    svgClasses="w-5 h-5 stroke-current cursor text-primary"
                    @click.stop="handleSelected1(accounts)"
                    class="icon_row"
                  />
                </div>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </vx-card>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { AtomSpinner } from "epic-spinners";
import moment from "moment";
import Datepicker from "vuejs-datepicker";
import vSelect from "vue-select";

export default {
  data() {
    return {
      loading: false,
      paginationItems: [10, 20, 30],
      delId: 0,
      deleteAccountPopup: false,
      selectedOffice: "",
      selectedAccount: "",
      selectedFilter: "",
      filterList: [
        { text: "All", value: "all" },
        { text: "Manual Entries", value: "manual" },
        { text: "System Entries", value: "system" },
      ],
      transactionId: "",
      toDate: "",
      fromDate: "",
    };
  },
  components: { AtomSpinner, vSelect, Datepicker },
  computed: {
    ...mapState({
      OfficesList: (state) => state.accounting.officesList,
      AllowedAccounts: (state) => state.accounting.allowedAccounts,
      JournalEntriesList: (state) => state.accounting.journalEntriesList,
      formattedDate: (state) => state.settings.selectedDateFormat,
    }),
    isSearchDisabled() {
      return !(
        this.selectedAccount ||
        this.selectedOffice ||
        this.fromDate ||
        this.toDate ||
        this.transactionId ||
        this.selectedFilter
      );
    },
  },
  methods: {
    getDate(date) {
      const d = new Date(date);
      return moment(d).format(this.formattedDate);
    },
    async getOfficeList() {
      try {
        this.$store.dispatch("accounting/getOfficesListAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    async getGLAccountsList() {
      try {
        this.$store.dispatch("accounting/getAllowedAccountsAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    handleClearAll() {
      this.selectedOffice = "";
      this.selectedAccount = "";
      this.selectedFilter = "";
      this.transactionId = "";
      this.toDate = "";
      this.fromDate = "";
    },
    async handleSearch() {
      const formatDate = (date) => moment(date).format("YYYY-MM-DD");
      let manualEntriesOnly = "";
      if (this.selectedFilter === "manual") {
        manualEntriesOnly = true;
      } else if (this.selectedFilter === "system") {
        manualEntriesOnly = false;
      }
      const options = {
        ...(this.selectedAccount && { glAccountId: this.selectedAccount }),
        ...(this.selectedOffice && { officeId: this.selectedOffice }),
        ...(this.fromDate && { fromDate: formatDate(this.fromDate) }),
        ...(this.toDate && { toDate: formatDate(this.toDate) }),
        ...(this.transactionId && { transactionId: this.transactionId }),
        ...(manualEntriesOnly !== "" && { manualEntriesOnly }),
      };
      try {
        this.$store.dispatch(
          "accounting/filterJournalEntriesListAction",
          options
        );
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    handleSelected1(data) {
      this.$router.push({
        name: "View Transaction",
        params: { id: data.transactionId, data: data },
      });
    },
  },
  watch: {},
  async mounted() {
    await this.$store.dispatch("accounting/clearJournalEntriesList");
    this.getOfficeList();
    this.getGLAccountsList();
  },
};
</script>
<style scoped>
::v-deep .vs-tabs--ul {
  width: 90%;
}
.inventory_tabSec >>> .con-vs-tabs .vs-tabs--ul {
  box-shadow: none !important;
}
.header-dropdown1 {
  min-width: 200px !important;
}
.row-align {
  align-items: baseline;
}
</style>
